import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  ISearchQuick,
  ISearchQuickPost,
  ISearchType,
} from "../../types/ISearch";
import { IUserProfile } from "../../types/User/IUserProfile";
import { IAssConfig, IUser } from "../../types/User/IUser";
import { Constants } from "../../Variables/StaticVariables";
import { ITenant } from "../../types/Tenant/ITenant";
import { IPriority } from "../../types/Priority/IPriority";
import { ISLA } from "../../types/ISLA";
import { ISearchField } from "../../types/ISearchField";
import { IFyndResult } from "../../types/IFyndResult";
import { IAssociatedContact } from "../../types/Ticket/ITicket";
import { RootState } from "../store";
import Cookies from 'js-cookie';
import { toast } from "react-toastify";
//import { TicketApiTags } from "./TicketApi";


const baseQuery = fetchBaseQuery({
  baseUrl: Constants.apiRoot,
  prepareHeaders: async (headers, { getState }) => {
    //console.log('prepareHeaders', (getState() as RootState).auth);
    const token = (getState() as RootState).auth.user?.access_token;
    //const token = (await userManager.getUser()).access_token;
    if (token !== "null" && token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  }
})

const baseQueryWithErrorHandling = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);
  if (result.error) {
    const { status } = result.error;
    if (status === 500) {
      toast.error(`Unexpected API error: ${Constants.apiRoot}${args}  \nError: ${result ? JSON.stringify(result.error) : 'Something went wrong'}`);
    }
  }


  // If we get a 429, wait 30 seconds before retrying
  if (result.error?.status === 429) {
    console.warn('Rate limited. Retrying in 10 seconds...');
    await new Promise((resolve) => setTimeout(resolve, 10000));
  }

  return result;
};

const TicketApiTags = ["GetTicketForm"]


const Api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    "SearchContacts",
    "ActionTypes",
    "ActionAuthors",
    "GetAction",
    "GetTicketAISummary",
    "GetReactions",
    "Stars",
    "Pins",
    "GetTicketConfig",
    "Chat",
    "Chats",
    "ChatMessages",
    "UserProfile",
    "UpdateSearchType",
    "QuickSearch",
    "UpdateStaff",
    "GetTenant",
    "ImageTicketTimeline",
    "ImageTimeline",
    "FormByProject",
    "ProjectList",
    "SLAList",
    "PriorityList",
    "ClientList",
    "GetPriority",
    "GetSLA",
    "GetTeams",
    "GetTeam",
    "UserFyndConfig",
    "GetProject",
    "GetStatuses",
    "GetTicket",
    "GetTicketDetails",
    "GetActions",
    "GetForm",
    "GetUsers",
    "TypeList",
    "SLAConfig",
    "GetAssociatedContacts",
    "GetProjectAssignment",
    "GetOpenTickets",
    "GetOpenParentTickets",
    "GetAssContactConfig",
    "GetClient",
    "GetUsersByIds",
    "GetUser",
    "MyLogOptions",
    "GetAssets",
    "UserRoles",
    "GetUsersById",
    "FormConfig",
    "GetUserSetting",
    "UpdateUserSetting",
    "GetClientTypes",
    "GetClientTypeFilter",
    "GetSystemHours",
    "GetQuickNotes",
    "GetMyTemplates",
    "GetTemplate",
    "GetTemplateAssignment",
    "GetTemplateActions",
    "GetStatus",
    "GetQuickNoteAsAction",
    "FileIsActive",
    "GetStopwatches",
    "GetSystemStartEnd",
    "GetFiles",
    "GetFileSetting",
    "KBSearch",
    "GetMyStatuses",
    "GetUserFeed",
    "GetUserFavs",
    "GetFeedConfig",
    "ProjectTypeList",
    "GetContacts",
    "GetFavConfig",
    "GetAlertConfig",
    "GetTenantConfig",
    "GetWorkstreams",
    "GetOffice",
    "GetClassification",
    "GetMyClient",
    "DebugTimezone",
    "GetHostConfig"
  ].concat(TicketApiTags),
  endpoints: (build) => ({
    getTenant: build.query<ITenant, string | void>({
      query: (tenantUid) => {
        const revTenantCookie = Cookies.get('RevTenantUid');
        return "Account/TenantConfig/?tenantUrl=" + document.location.host + "&tenantUid=" + (revTenantCookie || "")
      },
      providesTags: ["GetTenant"],
    }),
    renewlicense: build.mutation({
      query: () => ({
        url: "License/Renew",
        method: "POST",
      }),
    }),
    releaselicense: build.mutation({
      query: () => ({
        url: "License/Release",
        method: "POST",
      }),
    }),
    getAssContactConfig: build.query<IAssConfig, number>({
      query: (clientTypeId) => `ClientType/${clientTypeId}/AssociatedContacts`,
      providesTags: ["GetAssContactConfig"],
    }),
    getAssociatedContacts: build.query<
      IAssociatedContact[],
      { ticketId: number }
    >({
      query: (ticketId) => `Ticket/${ticketId}/Contact`,
      providesTags: ["GetAssociatedContacts"],
    }),
    getUserProfile: build.query<IUserProfile, string | void>({
      query: (uid) => {
        console.log('getUserProfile', uid);
        if (uid) {
          return `User/${uid}/Profile`;
        }
        return "User/Profile";
      },
      providesTags: ["UserProfile"],
    }),
    invalidateProfile: build.mutation<null, void>({
      queryFn: () => {
        console.log('invalidateProfile')
        return ({ data: null })
      },
      invalidatesTags: ["UserProfile"],
    }),
    updateFocusedViewToggle: build.mutation<IUserProfile, boolean>({
      query: (info) => ({
        url: `User/Me/ToggleNav`,
        method: "POST",
        body: { Collapsed: info },
      }),
      async onQueryStarted(info, { dispatch, queryFulfilled }) {
        const postResult = dispatch(
          Api.util.updateQueryData("getUserProfile", undefined, (draft) => {
            draft.Nav_Collapsed = info;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          postResult.undo();
        }
      },
      invalidatesTags: ["UserProfile"],
    }),
    getTicketTimelineImage: build.query<any, number>({
      query: (ticketId) => ({
        url: "Image/Timeline/" + ticketId,
        headers: { Accept: "image/jpeg" },
        responseHandler: async (response) => {
          const reader = new FileReader();
          return new Promise((resolve, reject) => {
            reader.onload = () => {
              const base64data = reader.result;
              resolve(base64data);
            };
            reader.onerror = (err) => {
              reject(err);
            };
            response.blob().then((value) => reader.readAsDataURL(value));
          });
        },
      }),
      providesTags: ["ImageTicketTimeline"],
    }),
    getTimelineImage: build.query<
      any,
      { projectId: number; slaId: number; priorityId: number }
    >({
      query: ({ projectId, slaId, priorityId }) => ({
        url: `Image/Timeline/${projectId}/${slaId ? slaId : 0}/${priorityId}/`,
        headers: { Accept: "image/jpeg" },
        responseHandler: async (response) => {
          const reader = new FileReader();
          return new Promise((resolve, reject) => {
            reader.onload = () => {
              const base64data = reader.result;
              resolve(base64data);
            };
            reader.onerror = (err) => {
              reject(err);
            };
            response.blob().then((value) => reader.readAsDataURL(value));
          });
        },
      }),
      providesTags: ["ImageTimeline"],
    }),
    getPriority: build.query<IPriority, number>({
      query: (priorityId: number) => `Ticket/Priority/${priorityId}`,
      providesTags: ["GetPriority"],
    }),
    getSLA: build.query<ISLA, number>({
      query: (slaId: number) => `SLA/${slaId}`,
      providesTags: ["GetSLA"],
    }),
    getSearchType: build.query<ISearchType[], void>({
      query: () => "Search/type",
      providesTags: ["UpdateSearchType"],
    }),
    getUserFyndConfig: build.query<ISearchField[], void>({
      query: () => "Search/UserFynd/Config",
      providesTags: ["UserFyndConfig"],
    }),
    getUsers: build.query<IUser[], { companyId: number; locationId?: number }>({
      query: ({ companyId, locationId }) =>
        `User/?companyId=${companyId}${locationId ? "&locationId=" + locationId : ""
        }`,
      providesTags: ["GetUsers"],
    }),
    // updateContactSetting: build.mutation<void, IContactSetting>({
    //   query: (info) => ({
    //     url: "User/Setting",
    //     method: "POST",
    //     body: info,
    //   }),
    // }),
    postUserFynd: build.mutation<IFyndResult[], String>({
      query: (info) => ({
        url: "Search/UserFynd",
        method: "POST",
        body: { SearchPhrase: info },
      }),
    }),
    postSearchQuick: build.mutation<ISearchQuick, ISearchQuickPost>({
      query: (info) => ({
        url: "Search/quick",
        method: "POST",
        body: info,
      }),
    }),
    updateSearchType: build.mutation<unknown, ISearchType[]>({
      query: (info) => ({
        url: "Search/type/my",
        method: "POST",
        body: info,
      }),
      async onQueryStarted(info, { dispatch, queryFulfilled }) {
        const postResult = dispatch(
          Api.util.updateQueryData("getSearchType", undefined, (draft) => {
            const selectOption = draft.find((item) => item.ID === info[0].ID);
            const checkAllBoolTrue = info.every((ele) => ele.Checked === true);
            if (draft.length === info.length) {
              // "Check All" Option was checked
              draft.forEach((item) => {
                if (checkAllBoolTrue) {
                  item.Checked = true;
                } else {
                  item.Checked = false;
                }
              });
            } else if (selectOption) {
              // Only a single Option was check
              selectOption.Checked = info[0].Checked;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          postResult.undo();
        }
      },
      invalidatesTags: ["UpdateSearchType"],
    }),
  }),
});

export const {
  useGetTicketTimelineImageQuery,
  useReleaselicenseMutation,
  useRenewlicenseMutation,
  useGetUserProfileQuery,
  useGetSearchTypeQuery,
  useUpdateSearchTypeMutation,
  usePostSearchQuickMutation,
  useGetTenantQuery,
  useUpdateFocusedViewToggleMutation,
} = Api;

export { Api };
